import React, {useEffect} from "react";

import styles from "./AssistantPage.module.css"
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {AssistantPageHeader} from "./assistantSettings/components/AssistantPageHeader/AssistantPageHeader";
import {botRequestStatusSelector, botSelector, fetchBotById} from "../botSlice";
import {FullBotUi} from "../../../../api/generated";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {WidgetAppearanceSettingsTab} from "./widgetSettings/WidgetAppearanceTab";
import {AssistantInformationTab} from "./assistantSettings/AssistantInformationTab";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {updateBotRequestStatusSelector} from "../botUpdateSlice";
import {
    fetchWidgetAppearanceSettingById,
    widgetAppearanceSettingRequestStatusSelector,
    widgetAppearanceSettingsSelector
} from "./widgetSettings/widgetAppearanceUpdateSlice";
import {
    fetchToolSettingsById,
    toolSettingsRequestStatusSelector,
    toolSettingsSelector
} from "./toolSettings/toolSettingsSlice";
import {ToolSettingsTab} from "./toolSettings/ToolSettingsTab";
import {AssistantIntegrationsTab} from "./integrations/AssistantIntegrationsTab";
import {
    botIntegrationDataListSelector,
    botIntegrationsRequestStatusSelector,
    fetchBotIntegrations
} from "./integrations/slice/integrationsSlice";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";
import {OfferLevel, OfferTable} from "../../offer/table/OfferTable";
import {LeadsTab} from "./leads/LeadsTab";
import {fetchLeadSettingsById, leadSettingsRequestStatusSelector} from "./leads/leadsSlice";
import {KnowledgeRecordsTab} from "../../knowledge/KnowledgeRecordsTab";
import {KnowledgeRecordUi, UpdateKnowledgeRecordRequest} from "../franchiseTypes";
import {updateKnowledgeRecords} from "../../knowledge/knowledgeApi";
import {TranscriptRoutingTab} from "./transcriptRouting/TranscriptRoutingTab";
import {KnowledgeScope} from "../../../../api/generated";
import {EmailSendingLogsTab} from "./emailSendingLogs/EmailSendingLogsTab";

export enum AssistantPageTabs {
    ASSISTANT = "ASSISTANT",
    APPEARANCE = "APPEARANCE",
    TOOL_SETTINGS = "TOOL_SETTINGS",
    OFFERS = "OFFERS",
    INTEGRATION = "INTEGRATION",
    LEADS = "LEADS",
    KNOWLEDGE = "KNOWLEDGE",
    TRANSCRIPT_ROUTING = "TRANSCRIPT_ROUTING",
    EMAIL_SENDING_LOGS = "EMAIL_SENDING_LOGS"
}

export const AssistantPage: React.FC = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const bot = useAppSelector(botSelector) as FullBotUi;
    const botRequestStatus = useAppSelector(botRequestStatusSelector);
    const updateBotRequestStatus = useAppSelector(updateBotRequestStatusSelector);

    const widgetAppearanceSettings = useAppSelector(widgetAppearanceSettingsSelector);
    const widgetAppearanceSettingsRequestStatus = useAppSelector(widgetAppearanceSettingRequestStatusSelector);

    const toolSettings = useAppSelector(toolSettingsSelector);
    const toolSettingsRequestStatus = useAppSelector(toolSettingsRequestStatusSelector);

    const botIntegrationDataList = useAppSelector(botIntegrationDataListSelector);
    const botIntegrationsRequestStatus = useAppSelector(botIntegrationsRequestStatusSelector);
    const leadSettingsRequestStatus = useAppSelector(leadSettingsRequestStatusSelector);

    const getTabFromHash = (): AssistantPageTabs => {
        const hash = location.hash.replace('#', '');
        return Object.values(AssistantPageTabs).includes(hash as AssistantPageTabs)
            ? hash as AssistantPageTabs
            : AssistantPageTabs.ASSISTANT;
    };

    const currentTab = getTabFromHash();

    const handleTabChange = (newTab: AssistantPageTabs) => {
        if (newTab === currentTab) return;

        if (newTab === AssistantPageTabs.APPEARANCE && assistantId) {
            dispatch(fetchWidgetAppearanceSettingById(assistantId));
        } else if (newTab === AssistantPageTabs.TOOL_SETTINGS && assistantId) {
            dispatch(fetchToolSettingsById(assistantId));
        } else if (newTab === AssistantPageTabs.LEADS && assistantId) {
            dispatch(fetchLeadSettingsById(assistantId));
        } else if (newTab === AssistantPageTabs.INTEGRATION && assistantId) {
            dispatch(fetchBotIntegrations(assistantId))
        }
    };

    const tabs = {
        [AssistantPageTabs.ASSISTANT]: {
            title: "Assistant set-up",
            onClick: () => handleTabChange(AssistantPageTabs.ASSISTANT),
            to: `#${AssistantPageTabs.ASSISTANT}`,
            isActive: currentTab === AssistantPageTabs.ASSISTANT,
            iconSrc: "/assistantIcon.svg"
        },
        [AssistantPageTabs.APPEARANCE]: {
            title: "Visual appearance",
            onClick: () => handleTabChange(AssistantPageTabs.APPEARANCE),
            to: `#${AssistantPageTabs.APPEARANCE}`,
            isActive: currentTab === AssistantPageTabs.APPEARANCE,
            iconSrc: "/appearanceIcon.svg"
        },
        [AssistantPageTabs.TOOL_SETTINGS]: {
            title: "Tool settings",
            onClick: () => handleTabChange(AssistantPageTabs.TOOL_SETTINGS),
            to: `#${AssistantPageTabs.TOOL_SETTINGS}`,
            isActive: currentTab === AssistantPageTabs.TOOL_SETTINGS,
            iconSrc: "/toolsIcon.svg"
        },
        [AssistantPageTabs.OFFERS]: {
            title: "Offers",
            onClick: () => handleTabChange(AssistantPageTabs.OFFERS),
            to: `#${AssistantPageTabs.OFFERS}`,
            isActive: currentTab === AssistantPageTabs.OFFERS,
            iconSrc: "/offersIcon.svg"
        },
        [AssistantPageTabs.LEADS]: {
            title: "Leads",
            onClick: () => handleTabChange(AssistantPageTabs.LEADS),
            to: `#${AssistantPageTabs.LEADS}`,
            isActive: currentTab === AssistantPageTabs.LEADS,
            iconSrc: "/leadIcon.svg"
        },
        [AssistantPageTabs.INTEGRATION]: {
            title: "Integrations",
            onClick: () => handleTabChange(AssistantPageTabs.INTEGRATION),
            to: `#${AssistantPageTabs.INTEGRATION}`,
            isActive: currentTab === AssistantPageTabs.INTEGRATION,
            iconSrc: "/integrationsIcon.svg"
        },
        [AssistantPageTabs.KNOWLEDGE]: {
            title: "Knowledge",
            onClick: () => handleTabChange(AssistantPageTabs.KNOWLEDGE),
            to: `#${AssistantPageTabs.KNOWLEDGE}`,
            isActive: currentTab === AssistantPageTabs.KNOWLEDGE,
            iconSrc: "/infoIcon.svg"
        },
        [AssistantPageTabs.TRANSCRIPT_ROUTING]: {
            title: "Transcript Routing",
            onClick: () => handleTabChange(AssistantPageTabs.TRANSCRIPT_ROUTING),
            to: `#${AssistantPageTabs.TRANSCRIPT_ROUTING}`,
            isActive: currentTab === AssistantPageTabs.TRANSCRIPT_ROUTING,
            iconSrc: "/transcriptRoutingIcon.svg"
        },
        [AssistantPageTabs.EMAIL_SENDING_LOGS]: {
            title: "Email logs",
            onClick: () => handleTabChange(AssistantPageTabs.EMAIL_SENDING_LOGS),
            to: `#${AssistantPageTabs.EMAIL_SENDING_LOGS}`,
            isActive: currentTab === AssistantPageTabs.EMAIL_SENDING_LOGS,
            iconSrc: "/emailSendingLogsIcon.svg"
        }
    }

    const {assistantId} = useParams<{ assistantId?: string }>();

    useEffect(() => {
        if (updateBotRequestStatus === RequestStatus.RESOLVED && assistantId) {
            dispatch(fetchBotById(assistantId));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateBotRequestStatus]);

    const reloadAssistant = () => {
        if (!assistantId) {
            return;
        }
        dispatch(fetchBotById(assistantId));
        dispatch(fetchWidgetAppearanceSettingById(assistantId));
        dispatch(fetchToolSettingsById(assistantId));
        dispatch(fetchLeadSettingsById(assistantId));
        dispatch(fetchBotIntegrations(assistantId));
    };

    useEffect(() => {
        if (assistantId) {
            reloadAssistant();
            dispatch(updateSelectedMenuItem(MenuItem.ASSISTANTS))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantId]);

    useEffect(() => {
        if (currentTab === AssistantPageTabs.APPEARANCE && assistantId) {
            dispatch(fetchWidgetAppearanceSettingById(assistantId));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]);

    useEffect(() => {
        if (botRequestStatus === RequestStatus.REJECTED || widgetAppearanceSettingsRequestStatus === RequestStatus.REJECTED || toolSettingsRequestStatus === RequestStatus.REJECTED || leadSettingsRequestStatus === RequestStatus.REJECTED) {
            navigate("/assistants")
            dispatch(showErrorToastNotification({message: "Failed to load assistant page"}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botRequestStatus, widgetAppearanceSettingsRequestStatus, toolSettingsRequestStatus, leadSettingsRequestStatus]);

    // Saving knowledge or feedback settings reloads the assistant. The tab stays mounted meanwhile, so it keeps
    // its page and the values still open for editing.
    const isReloadingInPlace = (currentTab === AssistantPageTabs.KNOWLEDGE || currentTab === AssistantPageTabs.APPEARANCE)
        && botRequestStatus === RequestStatus.PENDING
        && bot?.publicId === assistantId;
    const isBotInitialized = bot && (botRequestStatus === RequestStatus.RESOLVED || isReloadingInPlace);
    const isWidgetAppearanceSettingsInitialized = widgetAppearanceSettings && widgetAppearanceSettingsRequestStatus === RequestStatus.RESOLVED;
    const isToolSettingsInitialized = toolSettings && toolSettingsRequestStatus === RequestStatus.RESOLVED;
    const isLeadSettingsInitialized = leadSettingsRequestStatus === RequestStatus.RESOLVED;
    const isBotIntegrationsInitialized = botIntegrationDataList && botIntegrationsRequestStatus === RequestStatus.RESOLVED;

    const saveAssistantKnowledgeRecords = async (knowledgeRecords: UpdateKnowledgeRecordRequest[]) => {
        await updateKnowledgeRecords({
            scope: KnowledgeScope.Assistant,
            botPublicId: bot.publicId,
            knowledgeRecords
        });
        if (assistantId) {
            dispatch(fetchBotById(assistantId));
        }
    };

    if (isBotInitialized) {
        return (
            <div className={styles.wrapper}>
                <AssistantPageHeader bot={bot} tabs={tabs} onConfigurationImported={reloadAssistant}/>
                <div className={styles.content}>
                    {currentTab === AssistantPageTabs.ASSISTANT && <AssistantInformationTab bot={bot}/>}
                    {(currentTab === AssistantPageTabs.APPEARANCE && isWidgetAppearanceSettingsInitialized) &&
                        <WidgetAppearanceSettingsTab
                            widgetAppearanceSettings={widgetAppearanceSettings}
                            bot={bot}
                        />
                    }
                    {(currentTab === AssistantPageTabs.TOOL_SETTINGS && isToolSettingsInitialized) &&
                        <ToolSettingsTab bot={bot}/>
                    }
                    {(currentTab === AssistantPageTabs.LEADS && isLeadSettingsInitialized) &&
                        <LeadsTab bot={bot}/>
                    }
                    {currentTab === AssistantPageTabs.TRANSCRIPT_ROUTING && <TranscriptRoutingTab bot={bot}/>}
                    {currentTab === AssistantPageTabs.EMAIL_SENDING_LOGS && <EmailSendingLogsTab botPublicId={bot.publicId}/>}
                    {(currentTab === AssistantPageTabs.OFFERS) &&
                        <OfferTable
                            publicBotId={assistantId}
                            offerLevel={OfferLevel.ASSISTANT}
                        />
                    }
                    {(currentTab === AssistantPageTabs.INTEGRATION && isBotIntegrationsInitialized) &&
                        <AssistantIntegrationsTab
                            publicBotId={bot.publicId}
                            botIntegrationDataList={botIntegrationDataList}
                        />
                    }
                    {currentTab === AssistantPageTabs.KNOWLEDGE &&
                        <div className={styles.tabContent}>
                            <KnowledgeRecordsTab
                                records={(bot as FullBotUi & { knowledgeRecords?: KnowledgeRecordUi[] }).knowledgeRecords}
                                scope={KnowledgeScope.Assistant}
                                onSave={saveAssistantKnowledgeRecords}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    } else {
        return <Loader/>
    }
}
